<script setup lang="ts">
const coverArt = "/images/placeholder-release-cover.webp";
const muted = ref(true);
const videoEl = ref<HTMLVideoElement | null>(null);

function toggleMute() {
  muted.value = !muted.value;
}

const spotifyUrl = "https://open.spotify.com/track/52S6ay5ZiPG36TONHMmN5G?si=e9a063283c5d4873";
const youtubeUrl = "https://music.youtube.com/playlist?list=OLAK5uy_n3egkk6iJrcKiKPCpH7bUASTDAGf6_BNU&si=hYgIq6t6QhOpwMs9";
</script>

<template>
  <main class="bg-page pt-[72px]">
    <!-- HEADER (eyebrow + title + artist) -->
    <section class="max-w-4xl mx-auto px-4 md:px-6 pt-16 md:pt-24 flex flex-col items-center gap-2 md:gap-3">
      <p
        class="font-label font-light text-xs uppercase tracking-[5px] text-brand text-center"
      >
        WET WATER PRESENTS
      </p>
      <h1
        class="font-serif font-normal text-3xl md:text-8xl leading-tight text-dark text-center"
      >
        CANDY FOR BREAKFAST
      </h1>
      <p
        class="font-sans text-secondary text-sm md:text-base text-center"
      >
        A new release from Wet Set
      </p>
    </section>

    <!-- VIDEO TRAILER — full bleed -->
    <div class="relative aspect-[2/1] w-full h-160 overflow-hidden bg-dark mt-10 md:mt-14">
      <video
        ref="videoEl"
        src="/videos/CFB-02C.mp4"
        autoplay
        loop
        playsinline
        :muted="muted"
        class="w-full h-full object-cover"
      />
      <button
        class="absolute bottom-4 right-4 flex items-center gap-2 bg-dark/60 hover:bg-dark/80 text-white font-label font-light text-xs uppercase tracking-wider px-3 py-2 rounded transition-colors"
        @click="toggleMute"
      >
        <svg v-if="muted" viewBox="0 0 24 24" class="size-4" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06A8.99 8.99 0 0 0 17.73 19L19 20.27 20.27 19 5.27 4 4.27 3zM12 4 9.91 6.09 12 8.18V4z"/></svg>
        <svg v-else viewBox="0 0 24 24" class="size-4" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
        {{ muted ? 'Unmute' : 'Mute' }}
      </button>
    </div>

    <!-- COVER ARTWORK -->
    <section class="w-full flex justify-center px-4 md:px-6 mt-10 md:mt-14">
      <img
        :src="coverArt"
        alt="Candy for Breakfast cover"
        class="w-full max-w-sm mx-auto"
      />
    </section>

    <!-- DESCRIPTION -->
    <section class="max-w-4xl mx-auto px-4 md:px-6 mt-10 md:mt-14">
      <p
        class="font-sans text-dark text-base md:text-lg leading-7 md:leading-8 text-center max-w-2xl mx-auto"
      >
        Some mornings call for something sweet and a little off-schedule. Candy for Breakfast is Wet Set's first release — groove-forward, unhurried, anchored by a dry female vocal that sits in the room like morning light.
      </p>
    </section>

    <!-- STREAMING LINKS — full-bleed dark band -->
    <section class="w-full bg-dark mt-10 md:mt-14 py-12 md:py-16">
      <div class="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 px-4 md:px-6">
        <a
          :href="spotifyUrl"
          target="_blank"
          rel="noopener"
          class="font-label font-light text-base md:text-lg uppercase tracking-wider text-inverse hover:text-brand transition-colors inline-flex items-center gap-3"
        >
          <svg viewBox="0 0 24 24" class="size-8 md:size-10" aria-hidden="true">
            <path fill="#1DB954" d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12C24 5.4 18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
          SPOTIFY
        </a>
        <a
          :href="youtubeUrl"
          target="_blank"
          rel="noopener"
          class="font-label font-light text-base md:text-lg uppercase tracking-wider text-inverse hover:text-brand transition-colors inline-flex items-center gap-3"
        >
          <svg viewBox="0 0 24 24" class="size-8 md:size-10" aria-hidden="true">
            <path fill="#FF0000" d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 4.8c3.984 0 7.2 3.216 7.2 7.2s-3.216 7.2-7.2 7.2-7.2-3.216-7.2-7.2 3.216-7.2 7.2-7.2zm0 1.92c-2.928 0-5.28 2.352-5.28 5.28s2.352 5.28 5.28 5.28 5.28-2.352 5.28-5.28-2.352-5.28-5.28-5.28zM9.6 8.88l6.24 3.12L9.6 15.12V8.88z"/>
          </svg>
          YOUTUBE MUSIC
        </a>
      </div>
    </section>

    <!-- CREDITS -->
    <section class="max-w-4xl mx-auto px-4 md:px-6 mt-10 md:mt-14 pb-16 md:pb-24 flex flex-col gap-4">
      <div class="bg-stroke h-px w-full" />
      <p
        class="font-label font-light text-xs uppercase tracking-[5px] text-brand"
      >
        CREDITS
      </p>
      <div
        class="font-sans text-secondary text-sm leading-6 whitespace-pre-line"
      >
        [CREDITS PLACEHOLDER]
        Written by · [name]
        Produced by · [name]
        Mixed by · [name]
      </div>
    </section>
  </main>
</template>
